import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  Home, FileText, Heart, MessageSquare, Send, Pin, Plus, Trash2,
  Sparkles, Megaphone, PartyPopper, Book,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatDateTime } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { pedirExclusao } from '@/lib/confirmarExclusao'

type Aba = 'feed' | 'paginas'

interface Post {
  id: number
  autor_id: number
  autor_email?: string
  titulo?: string
  conteudo: string
  categoria?: string
  destaque: boolean
  curtidas: number
  curtiu: boolean
  num_comentarios: number
  criado_em: string
}

interface Pagina {
  id: number
  slug: string
  titulo: string
  conteudo: string
  publicada: boolean
}

const CATEGORIAS: Record<string, { label: string; Ic: any; cor: string }> = {
  comunicado: { label: 'Comunicado', Ic: Megaphone, cor: 'text-blue-600' },
  evento:     { label: 'Evento',     Ic: PartyPopper, cor: 'text-purple-600' },
  noticia:    { label: 'Notícia',    Ic: Sparkles,    cor: 'text-emerald-600' },
  manual:     { label: 'Manual',     Ic: Book,        cor: 'text-amber-600' },
}

export default function IntranetPage() {
  const [aba, setAba] = useState<Aba>('feed')
  const { user } = useAuth()
  const isAdmin = user?.perfil === 'admin' || user?.perfil === 'super_admin'

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-3 mb-2">
        <Home className="w-7 h-7 text-brand-600" />
        <div className="flex-1">
          <h1 className="text-2xl font-bold">Intranet</h1>
          <p className="text-sm text-ink-500">Comunicação interna e documentos da empresa</p>
        </div>
      </div>

      <div className="flex gap-1 border-b border-ink-200 mb-6">
        {[
          { id: 'feed', label: 'Feed', icon: Megaphone },
          { id: 'paginas', label: 'Páginas', icon: FileText },
        ].map(t => {
          const Ic = t.icon as any
          const ativo = aba === t.id
          return (
            <button key={t.id} onClick={() => setAba(t.id as Aba)}
              className={`px-4 py-2 text-sm font-medium flex items-center gap-2 border-b-2 transition ${
                ativo ? 'border-brand-600 text-brand-700' : 'border-transparent text-ink-500 hover:text-ink-800'
              }`}>
              <Ic className="w-4 h-4" /> {t.label}
            </button>
          )
        })}
      </div>

      {aba === 'feed' && <FeedPanel isAdmin={isAdmin} />}
      {aba === 'paginas' && <PaginasPanel isAdmin={isAdmin} />}
    </div>
  )
}

function FeedPanel({ isAdmin }: { isAdmin: boolean }) {
  const qc = useQueryClient()
  const { data: posts = [] } = useQuery<Post[]>({
    queryKey: ['intranet-posts'],
    queryFn: async () => (await api.get('/intranet/posts')).data,
  })

  const [novo, setNovo] = useState({ titulo: '', conteudo: '', categoria: 'comunicado', destaque: false })

  const criar = useMutation({
    mutationFn: async () => (await api.post('/intranet/posts', novo)).data,
    onSuccess: () => {
      toast.success('Publicado')
      setNovo({ titulo: '', conteudo: '', categoria: 'comunicado', destaque: false })
      qc.invalidateQueries({ queryKey: ['intranet-posts'] })
    },
  })

  return (
    <div className="space-y-4">
      {/* Composer */}
      <div className="card card-body">
        <div className="flex gap-2 mb-2">
          <select className="input !py-1 !text-sm w-40" value={novo.categoria}
            onChange={e => setNovo({ ...novo, categoria: e.target.value })}>
            {Object.entries(CATEGORIAS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <input className="input flex-1" placeholder="Título (opcional)"
            value={novo.titulo} onChange={e => setNovo({ ...novo, titulo: e.target.value })} />
        </div>
        <textarea className="input min-h-[80px]" placeholder="O que você quer comunicar?"
          value={novo.conteudo} onChange={e => setNovo({ ...novo, conteudo: e.target.value })} />
        <div className="flex items-center justify-between mt-2">
          {isAdmin && (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={novo.destaque}
                onChange={e => setNovo({ ...novo, destaque: e.target.checked })} />
              <Pin className="w-3 h-3" /> Fixar no topo
            </label>
          )}
          <button onClick={() => criar.mutate()} disabled={!novo.conteudo.trim() || criar.isPending}
            className="btn-primary ml-auto">
            <Send className="w-4 h-4" /> Publicar
          </button>
        </div>
      </div>

      {posts.map(p => <PostCard key={p.id} post={p} />)}
      {posts.length === 0 && (
        <div className="card card-body text-center py-12 text-ink-500">
          <Megaphone className="w-10 h-10 mx-auto mb-2 text-ink-300" />
          Ainda sem publicações. Seja o primeiro!
        </div>
      )}
    </div>
  )
}

function PostCard({ post }: { post: Post }) {
  const qc = useQueryClient()
  const { user } = useAuth()
  const [expComentarios, setExpComentarios] = useState(false)
  const [novoCom, setNovoCom] = useState('')
  const cat = CATEGORIAS[post.categoria || ''] || CATEGORIAS.comunicado
  const CatIc = cat.Ic

  const { data: comentarios = [] } = useQuery<any[]>({
    queryKey: ['intranet-coms', post.id],
    queryFn: async () => (await api.get(`/intranet/posts/${post.id}/comentarios`)).data,
    enabled: expComentarios,
  })

  const curtir = useMutation({
    mutationFn: async () => (await api.post(`/intranet/posts/${post.id}/curtir`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['intranet-posts'] }),
  })

  const comentar = useMutation({
    mutationFn: async () => (await api.post(`/intranet/posts/${post.id}/comentarios`, { texto: novoCom })).data,
    onSuccess: () => {
      setNovoCom('')
      qc.invalidateQueries({ queryKey: ['intranet-coms', post.id] })
      qc.invalidateQueries({ queryKey: ['intranet-posts'] })
    },
  })

  const apagar = useMutation({
    mutationFn: async () => (await api.delete(`/intranet/posts/${post.id}`)).data,
    onSuccess: () => { toast.success('Post apagado'); qc.invalidateQueries({ queryKey: ['intranet-posts'] }) },
  })

  const podeApagar = post.autor_id === user?.id || user?.perfil === 'admin' || user?.perfil === 'super_admin'

  return (
    <div className={`card card-body ${post.destaque ? 'border-amber-300 bg-amber-50/30' : ''}`}>
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-full bg-ink-100 flex items-center justify-center ${cat.cor}`}>
          <CatIc className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {post.destaque && <Pin className="w-3 h-3 text-amber-600" />}
            <span className="text-xs text-ink-500">{cat.label} · {post.autor_email} · {formatDateTime(post.criado_em)}</span>
          </div>
          {post.titulo && <h3 className="font-semibold mt-1">{post.titulo}</h3>}
          <div className="text-sm text-ink-700 mt-1 whitespace-pre-wrap">{post.conteudo}</div>
        </div>
        {podeApagar && (
          <button onClick={() => pedirExclusao({ descricao: 'o post', onConfirm: () => apagar.mutate() })}
            className="btn-ghost !p-1 text-rose-500">
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="flex items-center gap-4 mt-3 pt-3 border-t border-ink-100 text-sm">
        <button onClick={() => curtir.mutate()} className={`flex items-center gap-1 ${post.curtiu ? 'text-rose-600' : 'text-ink-500 hover:text-rose-600'}`}>
          <Heart className={`w-4 h-4 ${post.curtiu ? 'fill-current' : ''}`} /> {post.curtidas || 0}
        </button>
        <button onClick={() => setExpComentarios(!expComentarios)}
          className="flex items-center gap-1 text-ink-500 hover:text-brand-600">
          <MessageSquare className="w-4 h-4" /> {post.num_comentarios}
        </button>
      </div>

      {expComentarios && (
        <div className="mt-3 pt-3 border-t border-ink-100 space-y-2">
          {comentarios.map(c => (
            <div key={c.id} className="flex gap-2 text-sm">
              <div className="w-7 h-7 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center text-xs font-medium shrink-0">
                {c.autor_email?.[0]?.toUpperCase() || '?'}
              </div>
              <div className="flex-1 bg-ink-50 rounded-lg px-3 py-2">
                <div className="text-xs text-ink-500">{c.autor_email} · {formatDateTime(c.criado_em)}</div>
                <div className="text-sm">{c.texto}</div>
              </div>
            </div>
          ))}
          <div className="flex gap-2 pt-2">
            <input className="input flex-1 !py-1.5 text-sm" placeholder="Comentar..."
              value={novoCom} onChange={e => setNovoCom(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && novoCom.trim() && comentar.mutate()} />
            <button onClick={() => novoCom.trim() && comentar.mutate()}
              disabled={!novoCom.trim() || comentar.isPending}
              className="btn-primary !py-1.5 !px-3 text-xs">
              <Send className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function PaginasPanel({ isAdmin }: { isAdmin: boolean }) {
  const qc = useQueryClient()
  const nav = useNavigate()
  const { data: paginas = [] } = useQuery<Pagina[]>({
    queryKey: ['intranet-paginas'],
    queryFn: async () => (await api.get('/intranet/paginas')).data,
  })

  const [editando, setEditando] = useState<Pagina | null>(null)

  const salvar = useMutation({
    mutationFn: async () => {
      if (!editando) return
      if (editando.id) return (await api.put(`/intranet/paginas/${editando.id}`, editando)).data
      return (await api.post('/intranet/paginas', editando)).data
    },
    onSuccess: () => {
      toast.success('Página salva')
      setEditando(null)
      qc.invalidateQueries({ queryKey: ['intranet-paginas'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  const apagar = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/intranet/paginas/${id}`)).data,
    onSuccess: () => {
      toast.success('Página apagada')
      qc.invalidateQueries({ queryKey: ['intranet-paginas'] })
    },
  })

  return (
    <div className="space-y-3">
      {isAdmin && (
        <div className="flex justify-end">
          <button onClick={() => setEditando({ id: 0 as any, slug: '', titulo: '', conteudo: '', publicada: true })}
            className="btn-primary">
            <Plus className="w-4 h-4" /> Nova página
          </button>
        </div>
      )}
      {paginas.map(p => (
        <button key={p.id} onClick={() => isAdmin ? setEditando(p) : null}
          className={`card card-body w-full text-left ${isAdmin ? 'hover:border-brand-500' : ''} flex items-center gap-3`}>
          <FileText className="w-5 h-5 text-ink-400" />
          <div className="flex-1">
            <div className="font-semibold">{p.titulo}</div>
            <div className="text-xs text-ink-500 font-mono">/{p.slug}</div>
          </div>
          {!p.publicada && <span className="badge-neutral">rascunho</span>}
        </button>
      ))}
      {paginas.length === 0 && (
        <div className="card card-body text-center py-10 text-ink-500 text-sm">
          Sem páginas. {isAdmin && 'Clique em "Nova página" pra começar.'}
        </div>
      )}

      {editando && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setEditando(null)}>
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-ink-200 font-semibold">{editando.id ? 'Editar página' : 'Nova página'}</div>
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Slug *</label>
                  <input className="input font-mono" value={editando.slug}
                    onChange={e => setEditando({ ...editando, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                    placeholder="manual-funcionario" />
                </div>
                <div>
                  <label className="label">Título *</label>
                  <input className="input" value={editando.titulo}
                    onChange={e => setEditando({ ...editando, titulo: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="label">Conteúdo (markdown simples)</label>
                <textarea className="input min-h-[300px] font-mono text-sm" value={editando.conteudo}
                  onChange={e => setEditando({ ...editando, conteudo: e.target.value })} />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={editando.publicada}
                  onChange={e => setEditando({ ...editando, publicada: e.target.checked })} />
                Publicada
              </label>
            </div>
            <div className="p-4 border-t border-ink-200 flex justify-end gap-2 bg-ink-50">
              {editando.id && (
                <button onClick={() => pedirExclusao({ descricao: 'a página', onConfirm: () => { apagar.mutate(editando.id); setEditando(null) } })}
                  className="btn-ghost text-rose-600">
                  <Trash2 className="w-4 h-4" /> Apagar
                </button>
              )}
              <button onClick={() => setEditando(null)} className="btn-secondary">Cancelar</button>
              <button onClick={() => salvar.mutate()} disabled={salvar.isPending} className="btn-primary">
                <Send className="w-4 h-4" /> Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
